import React, { useEffect } from 'react'
import ReactDOM from 'react-dom'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { keys, isEmpty, isObject, isString } from 'lodash'
import { motion } from 'framer-motion'
import { useRouter } from 'next/router'
import { Element, scroller } from 'react-scroll'
import { Vacancy as VacancyIncludes } from 'includes'
import { useLocations } from 'lib/context/Location'
import withCommonStaticProps from 'lib/withCommonStaticProps'
import ApiService from 'lib/services'
import { trackFor } from 'lib/tracker/utils'

const useIsomorphicEffect = typeof window === 'undefined' ? React.useEffect : React.useLayoutEffect

const Vacancies = ({ pageData, departments }) => {
  const { query, ...router } = useRouter()
  const locations = useLocations().map(item => {
    const split = item.country.split('-')
    return {
      id: item.id.toString(),
      label: (item.city.trim() !== split[0].trim() ? item.city + ', ' : '').concat(split[0]),
    }
  })

  const types = [
    { id: 'Full-time', label: 'Full-time' },
    { id: 'Contract', label: 'Contract' },
    { id: 'Intern', label: 'Intern' },
  ]

  const imagesHero = keys(pageData)
    .filter(key => /^image_(?=.*[0-9])/.test(key) && pageData[key])
    .map(key =>
      isObject(pageData[key])
        ? pageData[key]
        : isString(pageData[key])
        ? { url: pageData[key] }
        : null,
    )

  const [jobs, setJobs] = React.useState({
    total: 0,
    perPage: 10,
    page: 1,
    lastPage: 1,
    data: [],
  })

  // derived data to be synced from URL searchParams
  // SSOT is always from searchParams, so we only call setParams inside routeChange
  const [params, setParams] = React.useState({
    page: 1,
    limit: 10,
  })
  const [loading, setLoading] = React.useState(false)
  const [activeField, setActiveField] = React.useState(null)

  useIsomorphicEffect(() => {
    let timeoutID
    function handleRouteChange() {
      setLoading(true)
      const config = {
        params: {
          page: 1,
          limit: 10,
        },
      }

      const queryMap = {
        department_id: 'department',
      }

      const validQueries = ['page', 'limit', 'search', 'location', 'department_id', 'job_type']
      const url = new URL(window.location.href)
      url.searchParams.forEach((value, key) => {
        if (validQueries.includes(key)) {
          const paramsKey = queryMap[key] || key
          config.params[paramsKey] = value
        }
      })

      // show what to render
      setParams(config.params)

      ApiService.get('/jobs', config)
        .then(({ data }) => {
          setJobs(data)

          const top5Data = data.data.slice(0, 5)

          if (Object.keys(config.params).length > 2) {
            trackFor({
              fbPixel: {
                event: 'Search',
                event_name: 'CareerSiteSearch',
                content_ids: top5Data.map(job => job.content.id),
                content_name: top5Data.map(job => job.content.title),
              },
              googleConversion: {
                conversion_label: 'mzDgCImKm8IDEP-ZgNAo',
                event_name: 'CareerSiteSearch',
              },
              linkedinInsight: {
                conversion_id: '9193457',
              },
              tiktokPixel: {
                event: 'Search',
                query: config.params,
              },
            })
          }
        })
        .catch(error => {
          console.error(error, 'red')
          setJobs({
            total: 0,
            perPage: 10,
            page: 1,
            lastPage: 1,
            data: [],
          })
        })
        .finally(() => {
          timeoutID = setTimeout(() => setLoading(false), 1200)
        })
    }

    // setup data for initial render
    handleRouteChange()

    router.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
      if (timeoutID !== undefined) {
        clearTimeout(timeoutID)
      }
    }
  }, [])

  useEffect(() => {
    trackFor({
      fbPixel: {
        event: 'CareerSiteVacancies',
        event_name: 'CareerSiteVacancies',
      },
      googleConversion: {
        conversion_label: 'esLuCNbE2MEDEP-ZgNAo',
        event_name: 'CareerSiteVacancies',
      },
      tiktokPixel: {
        event: 'ViewContent',
        content_name: 'Vacancies',
      },
    })
  }, [])

  function handleChangePage(page) {
    const url = new URL(window.location.href)
    url.searchParams.set('page', page)
    router.push('/vacancies' + url.search, undefined, {
      shallow: true,
    })
    scroller.scrollTo('scrollElementBox', {
      duration: 800,
      delay: 0,
      smooth: 'easeInOutQuart',
      offset: -50,
    })
  }

  function handleApplyFilters(params) {
    const search = params.search
    const location = isObject(params.location) ? params.location.id : params.location || ''
    const department = isObject(params.department)
      ? params.department.department_id
      : params.department || ''
    const jobType = isObject(params.job_type) ? params.job_type.id : params.job_type || ''

    const url = new URL(window.location.href)

    // reset pagination
    url.searchParams.set('page', 1)
    url.searchParams.set('limit', 10)

    if (!isEmpty(search) && isString(search)) {
      url.searchParams.set('search', search)
    } else {
      url.searchParams.delete('search')
    }

    if (!isEmpty(location) && isString(location)) {
      url.searchParams.set('location', location)
    } else {
      url.searchParams.delete('location')
    }

    if (!isEmpty(department) && isString(department)) {
      url.searchParams.set('department_id', department)
    } else {
      url.searchParams.delete('department_id')
    }

    if (!isEmpty(jobType) && isString(jobType)) {
      url.searchParams.set('job_type', jobType)
    } else {
      url.searchParams.delete('job_type')
    }

    router.push('/vacancies' + url.search, undefined, {
      shallow: true,
      scroll: false,
    })
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <VacancyIncludes.HeroHeader
        images={imagesHero}
        title={pageData.hero_title}
        description={pageData.description}
      />

      <VacancyIncludes.PageContent
        departments={departments}
        title={pageData.head_title}
        description={pageData.head_content}
        currentDepartment={params.department}
      />

      <Element name="scrollElementBox" className="element">
        <VacancyIncludes.List
          values={params}
          types={types}
          isLoading={loading}
          locations={locations}
          departments={departments}
          activeField={activeField}
          onToggleField={setActiveField}
          totalJobs={+jobs.total}
          jobsPerPage={jobs.perPage}
          currentPage={jobs.page}
          jobs={jobs.data}
          erros={''}
          handleChangePage={handleChangePage}
          handleApplyFilters={handleApplyFilters}
        />
      </Element>

      {typeof window !== 'undefined' &&
        ReactDOM.createPortal(
          <div
            onClick={() => setActiveField(null)}
            className={clsx('page-overlay', !isEmpty(activeField) && 'is-active')}
          />,
          document.getElementById('__next'),
        )}
    </motion.div>
  )
}

export const getStaticProps = async () => {
  const { data: departments } = await ApiService.get('/list-department')
  const { data: pageData } = await ApiService.get('/vacancies')

  return await withCommonStaticProps({
    props: {
      pageData,
      departments,
    },
  })
}

Vacancies.propTypes = {
  departments: PropTypes.array,
  pageData: PropTypes.object.isRequired,
}

export default Vacancies
